import _ from "lodash";

import dayjs from "dayjs";
import toObject from "dayjs/plugin/toObject";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(toObject);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export class DateConstructor {
  static FORMATS = {
    default: "DD/MM/YYYY HH:mm:ss",
    short: "DD/MM/YYYY HH:mm",
    mid: "DD MMM YYYY HH:mm",
    long: "D MMMM YYYY - HH:mm:ss",
    dateOnly: "DD/MM/YYYY",
    timeOnly: "HH:mm:ss",
    LT: "h:mm A", // 8:02 PM
    LTS: "h:mm:ss A", // 8:02:18 PM
    L: "MM/DD/YYYY", // 08/16/2018
    LL: "MMMM D, YYYY", // August 16, 2018
    LLL: "MMMM D, YYYY h:mm A", // August 16, 2018 8:02 PM
    LLLL: "dddd, MMMM D, YYYY h:mm A", // Thursday, August 16, 2018 8:02 PM
    l: "M/D/YYYY", // 8/16/2018
    ll: "MMM D, YYYY", // Aug 16, 2018
    lll: "MMM D, YYYY h:mm A", // Aug 16, 2018 8:02 PM
    llll: "ddd, MMM D, YYYY h:mm A", // Thu, Aug 16, 2018 8:02 PM
    ISO: "" // Thu, Aug 16, 2018 8:02 PM
  };

  setLocale(localeObjectOrString: string): void {
    dayjs.locale(localeObjectOrString);
  }

  setTimeZone(zone: string): void {
    dayjs.tz.setDefault(zone);
  }

  get lib() {
    return dayjs;
  }

  get FORMATS() {
    return DateConstructor.FORMATS;
  }

  getOutputFormat = (
    outputFormat: keyof typeof DateConstructor.FORMATS | string
  ): string =>
    _.defaultTo(
      DateConstructor.FORMATS[
        outputFormat as keyof typeof DateConstructor.FORMATS
      ],
      _.defaultTo(outputFormat, DateConstructor.FORMATS.default)
    );

  parse = (
    dateStringOrObject: string | Date | dayjs.Dayjs,
    inputFormat?: string
  ): Date => {
    const dateObj = dayjs(dateStringOrObject, inputFormat).toDate();
    return dateObj;
  };

  isValid = (
    dateStringOrObject: string | Date | dayjs.Dayjs,
    inputFormat?: string
  ): boolean => {
    return !!dateStringOrObject &&
      dayjs(dateStringOrObject, inputFormat).isValid();
  };

  format = (
    dateObject: string | Date | dayjs.Dayjs,
    outputFormat: keyof typeof DateConstructor.FORMATS | string,
    inputFormat?: string
  ): string => {
    const dateObj = this.parse(dateObject, inputFormat);
    const targetFormat = this.getOutputFormat(outputFormat);
    return dayjs(dateObj).format(targetFormat);
  };

  toObject = (
    dateStringOrObject: string | Date | dayjs.Dayjs,
    inputFormat?: string
  ) => {
    const dateObj = this.parse(dateStringOrObject, inputFormat);
    return dayjs(dateObj).toObject();
  };

  fromNow = (
    dateStringOrObject: string | Date | dayjs.Dayjs,
    inputFormat?: string,
    withoutSuffix: boolean = false
  ): string => {
    const dateObj = this.parse(dateStringOrObject, inputFormat);
    return dayjs(dateObj).fromNow(withoutSuffix);
  };

  add = (
    dateStringOrObject: string | Date | dayjs.Dayjs,
    amount: number,
    unit: dayjs.ManipulateType,
    outputFormat: keyof typeof DateConstructor.FORMATS | string
  ): string => {
    const targetFormat = this.getOutputFormat(outputFormat);
    return dayjs(dateStringOrObject).add(amount, unit)
      .format(targetFormat);
  };

  unixFormat = (
    unixTimeStamp: number,
    outputFormat: keyof typeof DateConstructor.FORMATS | string
  ): string => {
    const targetFormat = this.getOutputFormat(outputFormat);
    return dayjs.unix(unixTimeStamp).format(targetFormat);
  };
}

export default new DateConstructor();
